import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const DocumentConfigPanel = ({ template, onConfigChange, config }) => {
  const [activeTab, setActiveTab] = useState('scope');

  const tabs = [
    { id: 'scope', label: 'Scope Selection', icon: 'Target' },
    { id: 'requirements', label: 'Requirements', icon: 'CheckSquare' },
    { id: 'branding', label: 'Branding', icon: 'Palette' }
  ];

  const scopeItems = [
    {
      id: '1QM',
      name: 'Quality Management',
      description: 'Quality planning, inspection, and certificates',
      selected: true
    },
    {
      id: '5D2',
      name: 'Procurement to Pay',
      description: 'Purchase requisition to payment process',
      selected: true
    },
    {
      id: '2LX',
      name: 'Order to Cash',
      description: 'Sales order processing and fulfillment',
      selected: false
    }
  ];

  const requirements = [
    {
      id: 'req1',
      text: 'System must support multi-currency transactions',
      status: 'Fit',
      selected: true
    },
    {
      id: 'req2',
      text: 'Integration with external tax calculation service',
      status: 'Gap',
      selected: true
    },
    {
      id: 'req3',
      text: 'Custom approval workflow for high-value orders',
      status: 'Fit',
      selected: false
    }
  ];

  const handleScopeToggle = (itemId) => {
    const updatedScope = scopeItems?.map(item => 
      item?.id === itemId ? { ...item, selected: !item?.selected } : item
    );
    onConfigChange({ ...config, scope: updatedScope });
  };

  const handleRequirementToggle = (reqId) => {
    const updatedRequirements = requirements?.map(req => 
      req?.id === reqId ? { ...req, selected: !req?.selected } : req
    );
    onConfigChange({ ...config, requirements: updatedRequirements });
  };

  return (
    <div className="bg-card border border-border rounded-lg">
      <div className="border-b border-border p-4">
        <h3 className="text-lg font-semibold text-foreground">
          Configure {template?.name}
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Customize document content and appearance
        </p>
      </div>
      {/* Tabs */}
      <div className="border-b border-border">
        <nav className="flex space-x-8 px-4">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center space-x-2 py-3 border-b-2 text-sm font-medium transition-colors ${
                activeTab === tab?.id
                  ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </nav>
      </div>
      {/* Tab Content */}
      <div className="p-4">
        {activeTab === 'scope' && (
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">
                Select SAP Best Practice Scope Items
              </h4>
              <div className="space-y-3">
                {scopeItems?.map((item) => (
                  <div key={item?.id} className="flex items-start space-x-3 p-3 border border-border rounded-lg">
                    <Checkbox
                      checked={item?.selected}
                      onChange={() => handleScopeToggle(item?.id)}
                    />
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-foreground">{item?.id}</span>
                        <span className="text-sm text-foreground">{item?.name}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{item?.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'requirements' && (
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">
                Include Requirements from Fit/Gap Analysis
              </h4>
              <div className="space-y-3">
                {requirements?.map((req) => (
                  <div key={req?.id} className="flex items-start space-x-3 p-3 border border-border rounded-lg">
                    <Checkbox
                      checked={req?.selected}
                      onChange={() => handleRequirementToggle(req?.id)}
                    />
                    <div className="flex-1">
                      <p className="text-sm text-foreground">{req?.text}</p>
                      <div className="flex items-center space-x-2 mt-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          req?.status === 'Fit' ?'bg-green-100 text-green-700' :'bg-red-100 text-red-700'
                        }`}>
                          {req?.status}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'branding' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Organization Name"
                type="text"
                placeholder="Enter organization name"
                value={config?.organizationName || ''}
                onChange={(e) => onConfigChange({ ...config, organizationName: e?.target?.value })}
              />
              <Input
                label="Project Name"
                type="text"
                placeholder="Enter project name"
                value={config?.projectName || ''}
                onChange={(e) => onConfigChange({ ...config, projectName: e?.target?.value })}
              />
              <Input
                label="Document Version"
                type="text"
                placeholder="1.0"
                value={config?.version || ''}
                onChange={(e) => onConfigChange({ ...config, version: e?.target?.value })}
              />
              <Input
                label="Author"
                type="text"
                placeholder="Enter author name"
                value={config?.author || ''}
                onChange={(e) => onConfigChange({ ...config, author: e?.target?.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Logo Upload
              </label>
              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                <Icon name="Upload" size={32} className="text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">
                  Drag and drop your logo here, or click to browse
                </p>
                <Button variant="outline" size="sm" className="mt-2">
                  Choose File
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentConfigPanel;